import { useEffect, useState } from 'react';
import axios from 'axios';
import { 
  Package, Clock, Truck, CheckCircle, XCircle, RotateCcw, 
  Search, Filter, Eye, FileText, MessageCircle, ChevronDown,
  Send, Download
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ORDER_STATUS_FLOW = [
  { key: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  { key: 'confirmed', label: 'Confirmed', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
  { key: 'processing', label: 'Processing', color: 'bg-indigo-100 text-indigo-800', icon: Package },
  { key: 'packed', label: 'Packed', color: 'bg-purple-100 text-purple-800', icon: Package },
  { key: 'shipped', label: 'Shipped', color: 'bg-cyan-100 text-cyan-800', icon: Truck },
  { key: 'out_for_delivery', label: 'Out for Delivery', color: 'bg-orange-100 text-orange-800', icon: Truck },
  { key: 'delivered', label: 'Delivered', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  { key: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-800', icon: XCircle },
  { key: 'returned', label: 'Returned', color: 'bg-gray-100 text-gray-800', icon: RotateCcw }
];

export const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [trackingId, setTrackingId] = useState('');
  const [courierPartner, setCourierPartner] = useState('');
  const [orderNote, setOrderNote] = useState('');
  const [updating, setUpdating] = useState(false);

  const COURIER_PARTNERS = [
    'Delhivery',
    'Xpressbees',
    'DTDC',
    'Blue Dart',
    'India Post',
    'Shadowfax',
    'Ecom Express',
    'Professional Couriers',
    'Other'
  ];

  useEffect(() => {
    loadOrders();
    loadStats();
  }, []);

  const loadOrders = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Enrich order items with product images if missing
      const ordersData = response.data || [];
      const productCache = {};
      
      for (const order of ordersData) {
        for (const item of order.items || []) {
          if (!item.image && item.product_id) {
            // Fetch product data if not in cache
            if (!productCache[item.product_id]) {
              try {
                const productRes = await axios.get(`${API}/products/${item.product_id}`);
                productCache[item.product_id] = productRes.data;
              } catch (e) {
                productCache[item.product_id] = null;
              }
            }
            
            // Get image from product
            const product = productCache[item.product_id];
            if (product) {
              item.image = product.images?.[0] || product.image || null;
              // Also get EAN if available
              if (!item.ean && product.variants) {
                const variant = product.variants.find(v => 
                  v.size === item.size && v.color === item.color
                ) || product.variants[0];
                if (variant) {
                  item.ean = variant.ean || variant.sku || null;
                }
              }
            }
          }
        }
      }
      
      setOrders(ordersData);
    } catch (error) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/orders/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data.stats || {});
    } catch (error) {
      console.error('Failed to load stats');
    }
  };

  const openStatusModal = (order) => {
    setSelectedOrder(order);
    setNewStatus(order.order_status);
    setTrackingId(order.tracking_id || order.tracking_number || '');
    setCourierPartner(order.courier_partner || '');
    setOrderNote('');
    setShowStatusModal(true);
  };

  const updateOrderStatus = async () => {
    if (!newStatus) return;
    
    setUpdating(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/admin/orders/${selectedOrder.id}/update-status`, {
        status: newStatus,
        tracking_id: trackingId || undefined,
        courier_partner: courierPartner || undefined,
        notes: orderNote || undefined
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Order status updated');
      setShowStatusModal(false);
      loadOrders();
      loadStats();
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const downloadInvoice = async (orderId, orderNumber) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/orders/${orderId}/invoice`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${orderNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Invoice downloaded');
    } catch (error) {
      toast.error('Failed to download invoice');
    }
  };

  const generateWhatsAppMessage = (order, status) => {
    const customerName = order.customer_name?.split(' ')[0] || 'Customer';
    const orderId = order.order_number || order.id;
    const courier = order.courier_partner || courierPartner || 'Our courier partner';
    const tracking = order.tracking_id || trackingId || 'Will be updated soon';
    
    const messages = {
      confirmed: `Hello ${customerName},

Your order has been confirmed! ✅

Order ID: ${orderId}

Thank you for shopping with JAT Fashion House.

We will notify you once your order is dispatched.`,

      processing: `Hello ${customerName},

Your order is now being prepared. 📦

Order ID: ${orderId}

We're carefully packing your items with love!`,

      packed: `Good news, ${customerName}! 🎉

Your order has been packed and is ready for dispatch.

Order ID: ${orderId}

It will be shipped soon!`,

      shipped: `Hello ${customerName},

Your order has been shipped! 🚚

Order ID: ${orderId}
Courier: ${courier}
Tracking ID: ${tracking}

Track your shipment using your courier's website or app.`,

      out_for_delivery: `Hello ${customerName},

Great news! Your order is out for delivery today! 🎊

Order ID: ${orderId}

Please keep your phone available for the delivery executive.`,

      delivered: `Hello ${customerName},

Your order has been delivered! ✨

Order ID: ${orderId}

Thank you for shopping with JAT Fashion House!

We would love your feedback. 🙏`,

      cancelled: `Hello ${customerName},

Your order has been cancelled.

Order ID: ${orderId}

If you have any questions or need assistance, please contact us.

We apologize for any inconvenience caused.`,

      returned: `Hello ${customerName},

Your returned order has been received.

Order ID: ${orderId}

Thank you for shopping with JAT Fashion House.

If you need any assistance, please contact us.`
    };

    return messages[status] || `Hello ${customerName},

Order update for Order ID: ${orderId}

Status: ${status}

Thank you for shopping with JAT Fashion House.`;
  };

  const openWhatsAppForStatus = (order, status) => {
    const phone = order.customer_phone?.replace(/[^0-9]/g, '') || '';
    const phoneNumber = phone.startsWith('91') ? phone : `91${phone}`;
    const message = generateWhatsAppMessage(order, status);
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const sendWhatsAppNotification = async (orderId, type = 'status_update') => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API}/admin/orders/${orderId}/send-whatsapp`, {
        type
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      window.open(response.data.whatsapp_link, '_blank');
      toast.success('WhatsApp notification prepared');
    } catch (error) {
      toast.error('Failed to send notification');
    }
  };

  const getStatusConfig = (status) => {
    return ORDER_STATUS_FLOW.find(s => s.key === status) || ORDER_STATUS_FLOW[0];
  };

  const filteredOrders = orders.filter(order => {
    const matchesStatus = !statusFilter || order.order_status === statusFilter;
    const matchesSearch = !searchQuery || 
      order.order_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.customer_email?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-orders">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-light text-charcoal">Order Management</h1>
        <p className="text-charcoal-light text-sm mt-1">Manage and track all orders</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {ORDER_STATUS_FLOW.slice(0, 5).map(status => (
          <button
            key={status.key}
            onClick={() => setStatusFilter(statusFilter === status.key ? '' : status.key)}
            className={`p-4 border text-center transition-colors ${
              statusFilter === status.key 
                ? 'border-gold bg-gold/5' 
                : 'border-charcoal/10 hover:border-gold/50'
            }`}
          >
            <status.icon className={`w-5 h-5 mx-auto mb-2 ${statusFilter === status.key ? 'text-gold' : 'text-charcoal-light'}`} />
            <p className="font-heading text-2xl font-light text-charcoal">{stats[status.key] || 0}</p>
            <p className="text-xs text-charcoal-light mt-1">{status.label}</p>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white border border-charcoal/10 p-4 flex flex-wrap gap-4 items-center">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-charcoal-light" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by order #, customer..."
            className="w-full h-10 pl-10 pr-4 border border-charcoal/10 focus:border-gold outline-none"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-10 px-4 border border-charcoal/10 focus:border-gold outline-none bg-white"
        >
          <option value="">All Status</option>
          {ORDER_STATUS_FLOW.map(status => (
            <option key={status.key} value={status.key}>{status.label}</option>
          ))}
        </select>
      </div>

      {/* Orders Table */}
      <div className="bg-white border border-charcoal/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-beige/50">
              <tr>
                <th className="text-left px-5 py-3 text-xs tracking-wider uppercase text-charcoal-light font-medium">Order</th>
                <th className="text-left px-5 py-3 text-xs tracking-wider uppercase text-charcoal-light font-medium">Customer</th>
                <th className="text-left px-5 py-3 text-xs tracking-wider uppercase text-charcoal-light font-medium">Items</th>
                <th className="text-left px-5 py-3 text-xs tracking-wider uppercase text-charcoal-light font-medium">Total</th>
                <th className="text-left px-5 py-3 text-xs tracking-wider uppercase text-charcoal-light font-medium">Payment</th>
                <th className="text-left px-5 py-3 text-xs tracking-wider uppercase text-charcoal-light font-medium">Status</th>
                <th className="text-left px-5 py-3 text-xs tracking-wider uppercase text-charcoal-light font-medium">Date</th>
                <th className="text-left px-5 py-3 text-xs tracking-wider uppercase text-charcoal-light font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-charcoal/5">
              {filteredOrders.length > 0 ? (
                filteredOrders.map((order) => {
                  const statusConfig = getStatusConfig(order.order_status);
                  return (
                    <tr key={order.id} className="hover:bg-beige/30 transition-colors">
                      <td className="px-5 py-4">
                        <span className="text-sm font-medium text-charcoal">#{order.order_number}</span>
                      </td>
                      <td className="px-5 py-4">
                        <p className="text-sm text-charcoal">{order.customer_name}</p>
                        <p className="text-xs text-charcoal-light">{order.customer_phone}</p>
                      </td>
                      <td className="px-5 py-4">
                        <span className="text-sm text-charcoal">{order.items?.length || 0} items</span>
                      </td>
                      <td className="px-5 py-4">
                        <span className="text-sm font-medium text-charcoal">{formatCurrency(order.total_amount)}</span>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`text-xs px-2 py-1 ${
                          order.payment_status === 'completed' ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'
                        }`}>
                          {order.payment_method?.toUpperCase()} - {order.payment_status}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium ${statusConfig.color}`}>
                          <statusConfig.icon className="w-3 h-3" />
                          {statusConfig.label}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <span className="text-sm text-charcoal-light">{formatDate(order.created_at)}</span>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => openStatusModal(order)}
                            className="p-1.5 text-charcoal-light hover:text-gold hover:bg-beige transition-colors"
                            title="Update Status"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => downloadInvoice(order.id, order.order_number)}
                            className="p-1.5 text-charcoal-light hover:text-gold hover:bg-beige transition-colors"
                            title="Download Invoice"
                          >
                            <FileText className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => sendWhatsAppNotification(order.id)}
                            className="p-1.5 text-charcoal-light hover:text-green-600 hover:bg-green-50 transition-colors"
                            title="Send WhatsApp"
                          >
                            <MessageCircle className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={8} className="px-5 py-12 text-center text-charcoal-light">
                    No orders found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Status Update Modal */}
      {showStatusModal && selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/50">
          <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-charcoal/10 p-6">
              <h2 className="font-heading text-xl font-light text-charcoal">
                Order #{selectedOrder.order_number}
              </h2>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Order Details */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-charcoal-light">Customer:</span>
                  <p className="text-charcoal font-medium">{selectedOrder.customer_name}</p>
                  <p className="text-charcoal-light">{selectedOrder.customer_email}</p>
                  <p className="text-charcoal-light">{selectedOrder.customer_phone}</p>
                </div>
                <div>
                  <span className="text-charcoal-light">Shipping Address:</span>
                  <p className="text-charcoal whitespace-pre-line">{selectedOrder.shipping_address}</p>
                </div>
              </div>

              {/* Items - Enhanced Display */}
              <div>
                <h4 className="text-xs tracking-wider uppercase text-charcoal-light font-medium mb-3">Order Items</h4>
                <div className="border border-charcoal/10 divide-y divide-charcoal/5">
                  {selectedOrder.items?.map((item, idx) => (
                    <div key={idx} className="p-4 flex gap-4">
                      <div className="w-20 h-20 bg-beige border border-charcoal/10 flex items-center justify-center overflow-hidden">
                        {item.image ? (
                          <img 
                            src={item.image} 
                            alt={item.product_name} 
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div className={`w-full h-full items-center justify-center text-charcoal-light ${item.image ? 'hidden' : 'flex'}`}>
                          <Package className="w-8 h-8" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-charcoal">{item.product_name}</p>
                        <div className="mt-1.5 space-y-0.5">
                          <p className="text-xs text-charcoal-light">
                            <span className="inline-block w-16 font-medium">SKU/EAN:</span> 
                            {item.ean || item.sku || item.variant_id?.slice(0, 8) || 'N/A'}
                          </p>
                          <p className="text-xs text-charcoal-light">
                            <span className="inline-block w-16 font-medium">Size:</span> {item.size || 'Free Size'}
                          </p>
                          <p className="text-xs text-charcoal-light">
                            <span className="inline-block w-16 font-medium">Color:</span> {item.color || 'Standard'}
                          </p>
                          <p className="text-xs text-charcoal-light">
                            <span className="inline-block w-16 font-medium">Quantity:</span> {item.quantity}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-charcoal">{formatCurrency(item.price * item.quantity)}</p>
                        <p className="text-xs text-charcoal-light mt-1">{formatCurrency(item.price)} × {item.quantity}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Status Update */}
              <div>
                <h4 className="text-xs tracking-wider uppercase text-charcoal-light font-medium mb-3">Update Status</h4>
                <div className="grid grid-cols-3 gap-2">
                  {ORDER_STATUS_FLOW.map(status => (
                    <button
                      key={status.key}
                      onClick={() => setNewStatus(status.key)}
                      className={`p-3 border text-center text-sm transition-colors ${
                        newStatus === status.key 
                          ? 'border-gold bg-gold/10 text-gold' 
                          : 'border-charcoal/10 hover:border-gold/50 text-charcoal-light'
                      }`}
                    >
                      <status.icon className="w-4 h-4 mx-auto mb-1" />
                      {status.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tracking & Courier Information */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
                    Tracking ID / AWB Number
                  </label>
                  <input
                    type="text"
                    value={trackingId}
                    onChange={(e) => setTrackingId(e.target.value)}
                    className="w-full h-10 px-4 border border-charcoal/10 focus:border-gold outline-none"
                    placeholder="Enter tracking/AWB number"
                  />
                </div>
                <div>
                  <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
                    Courier Partner
                  </label>
                  <select
                    value={courierPartner}
                    onChange={(e) => setCourierPartner(e.target.value)}
                    className="w-full h-10 px-4 border border-charcoal/10 focus:border-gold outline-none bg-white"
                  >
                    <option value="">Select Courier</option>
                    {COURIER_PARTNERS.map(courier => (
                      <option key={courier} value={courier}>{courier}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Order Note */}
              <div>
                <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
                  Add Note (Optional)
                </label>
                <textarea
                  value={orderNote}
                  onChange={(e) => setOrderNote(e.target.value)}
                  className="w-full h-20 px-4 py-2 border border-charcoal/10 focus:border-gold outline-none resize-none"
                  placeholder="Internal note about this order..."
                />
              </div>

              {/* Notes History */}
              {selectedOrder.notes_history?.length > 0 && (
                <div>
                  <h4 className="text-xs tracking-wider uppercase text-charcoal-light font-medium mb-3">Notes History</h4>
                  <div className="space-y-2">
                    {selectedOrder.notes_history.map((note, idx) => (
                      <div key={idx} className="bg-beige/50 p-3 text-sm">
                        <p className="text-charcoal">{note.note}</p>
                        <p className="text-xs text-charcoal-light mt-1">
                          {note.by} • {note.status} • {formatDate(note.timestamp)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div className="sticky bottom-0 bg-white border-t border-charcoal/10 p-6 flex justify-between">
              <div className="flex gap-2">
                <button
                  onClick={() => downloadInvoice(selectedOrder.id, selectedOrder.order_number)}
                  className="flex items-center gap-2 px-4 py-2 border border-charcoal/20 text-charcoal text-sm hover:bg-beige"
                >
                  <Download className="w-4 h-4" />
                  Invoice
                </button>
                <button
                  onClick={() => openWhatsAppForStatus({
                    ...selectedOrder,
                    tracking_id: trackingId,
                    courier_partner: courierPartner
                  }, newStatus)}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm hover:bg-green-700"
                  title="Send status update via WhatsApp"
                >
                  <Send className="w-4 h-4" />
                  Notify Customer
                </button>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowStatusModal(false)}
                  className="px-6 py-2 border border-charcoal/20 text-charcoal text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={updateOrderStatus}
                  disabled={updating}
                  className="px-6 py-2 bg-gold text-white text-sm disabled:opacity-50"
                >
                  {updating ? 'Updating...' : 'Update Status'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
